import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@ludolio/shared-ui';
import { EmptyState, SectionTitle, TeamRoleBadge } from '@/components/common';
import { getErrorMessage, labsApi } from '@/lib/api';
import { useLabs } from '@/lib/labs-context';
import type { LabTeamDetail, LabTeamRole } from '@/lib/types';

export const Route = createFileRoute('/$orgSlug/teams/$teamId')({
  component: TeamPage,
});

function TeamPage() {
  const { teamId } = Route.useParams();
  const labs = useLabs();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<LabTeamDetail | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'manager'>('member');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pendingRole, setPendingRole] = useState<{ userId: string; role: LabTeamRole } | null>(null);
  const [claiming, setClaiming] = useState(false);

  const backToTeams = useCallback(
    () => navigate({ to: '/$orgSlug/teams', params: { orgSlug: labs.organization.slug } }),
    [labs.organization.slug, navigate],
  );

  const apply = useCallback((next: LabTeamDetail) => {
    setDetail(next);
    setName(next.team.name);
    setDescription(next.team.description ?? '');
  }, []);

  useEffect(() => {
    let active = true;
    labsApi
      .getTeam(teamId)
      .then((next) => {
        if (active) apply(next);
      })
      .catch((err) => {
        if (active) setError(getErrorMessage(err, 'This team could not be loaded.'));
      });
    return () => {
      active = false;
    };
  }, [apply, teamId]);

  const run = async (action: () => Promise<LabTeamDetail | null>, success?: string) => {
    setBusy(true);
    try {
      const next = await action();
      if (!next) {
        backToTeams();
        return;
      }
      apply(next);
      if (success) toast.success(success);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (error && !detail) {
    return <EmptyState title={error} action={<Button variant="outline" onClick={backToTeams}>Back to teams</Button>} />;
  }
  if (!detail) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const activeOwnerCount = detail.members.filter((member) => member.role === 'owner' && member.inOrganization).length;
  const pendingMember = pendingRole ? detail.members.find((member) => member.userId === pendingRole.userId) : undefined;
  const pendingIsSelf = pendingMember?.userId === labs.user.id;
  const canRemoveMember = (member: LabTeamDetail['members'][number]) => {
    const isSelf = member.userId === labs.user.id;
    const lastOwner = member.role === 'owner' && member.inOrganization && activeOwnerCount <= 1;
    return !lastOwner && (isSelf || detail.role === 'owner' || (detail.role === 'manager' && member.role === 'member'));
  };
  const rowColumns = detail.members.some(canRemoveMember)
    ? 'sm:grid-cols-[minmax(0,1fr)_9rem_9.5rem_5rem]'
    : 'sm:grid-cols-[minmax(0,1fr)_9rem_9.5rem]';
  const detailsDirty = name.trim() !== detail.team.name || (description.trim() || null) !== detail.team.description;

  const saveDetails = () => run(
    () => labsApi.updateTeam(detail.team.id, { name: name.trim(), description: description.trim() || null }),
    'Team updated',
  );

  return (
    <div className="space-y-8">
      <Link
        to="/$orgSlug/teams"
        params={{ orgSlug: labs.organization.slug }}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Teams
      </Link>

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="truncate text-2xl font-semibold tracking-tight">{detail.team.name}</h1>
        <TeamRoleBadge role={detail.role} />
      </div>

      {!detail.hasActiveOwner && (
        <div
          role="status"
          className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200 sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <p className="font-medium">This team has no active owner.</p>
            {!detail.permissions.canClaimOwnership && (
              <p className="text-sm">A manager can become owner from this page, or ask your librarian.</p>
            )}
          </div>
          {detail.permissions.canClaimOwnership && (
            <Button disabled={busy} onClick={() => setClaiming(true)} className="sm:shrink-0">
              Become owner
            </Button>
          )}
        </div>
      )}

      {detail.permissions.canEditTeam ? (
        <form
          className="grid max-w-xl gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void saveDetails();
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="team-name">Name</Label>
            <Input id="team-name" value={name} maxLength={120} onChange={(event) => setName(event.target.value)} disabled={busy} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="team-description">Description</Label>
            <Textarea
              id="team-description"
              value={description}
              maxLength={2000}
              rows={3}
              onChange={(event) => setDescription(event.target.value)}
              disabled={busy}
            />
          </div>
          <div>
            <Button type="submit" disabled={busy || !detailsDirty || !name.trim()}>Save</Button>
          </div>
        </form>
      ) : (
        detail.team.description && <p className="max-w-2xl text-sm text-muted-foreground">{detail.team.description}</p>
      )}

      <section>
        <SectionTitle>Members</SectionTitle>
        <ul className="divide-y rounded-lg border">
          {detail.members.map((member) => {
            const isSelf = member.userId === labs.user.id;
            const canRemove = canRemoveMember(member);
            const canChangeRole = detail.permissions.canManageRoles && (!isSelf || activeOwnerCount > 1);
            return (
              <li key={member.userId} className={`grid items-center gap-3 p-4 ${rowColumns}`}>
                <div className="min-w-0">
                  <p className="truncate font-medium">{member.name}{isSelf ? ' (you)' : ''}</p>
                  <p className="truncate text-sm text-muted-foreground">{member.email}</p>
                </div>
                <div className={member.inOrganization ? 'hidden sm:block' : ''}>
                  {!member.inOrganization && <Badge variant="outline">Left the university</Badge>}
                </div>
                {canChangeRole ? (
                  <Select
                    value={member.role}
                    onValueChange={(value) => {
                      const role = value as LabTeamRole;
                      if (role === member.role) return;
                      if (role === 'owner' || isSelf) {
                        setPendingRole({ userId: member.userId, role });
                        return;
                      }
                      void run(() => labsApi.setTeamRole(detail.team.id, member.userId, role));
                    }}
                    disabled={busy}
                  >
                    <SelectTrigger aria-label={`Role for ${member.name}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      {(member.inOrganization || member.role === 'owner') && <SelectItem value="owner">Owner</SelectItem>}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="sm:justify-self-start">
                    <TeamRoleBadge role={member.role} />
                  </div>
                )}
                <div className="flex items-center justify-end gap-2 empty:hidden">
                  {canRemove && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-20"
                      disabled={busy}
                      onClick={() => void run(() => labsApi.removeTeamMember(detail.team.id, member.userId))}
                    >
                      {isSelf ? 'Leave' : 'Remove'}
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {detail.permissions.canManageMembers && (
        <form
          className={
            detail.permissions.canManageRoles
              ? 'grid max-w-xl gap-3 sm:grid-cols-[minmax(0,1fr)_9rem_auto] sm:items-end'
              : 'flex flex-col gap-3 sm:flex-row sm:items-end'
          }
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              const next = await labsApi.addTeamMember(detail.team.id, email.trim(), inviteRole);
              setEmail('');
              return next;
            });
          }}
        >
          {/* grid gap, not space-y: Radix Select appends a hidden <select>, which gives the trigger a bottom margin under space-y. */}
          <div className="grid min-w-0 flex-1 gap-2">
            <Label htmlFor="member-email">Add a member</Label>
            <Input
              id="member-email"
              type="email"
              value={email}
              placeholder="name@university.edu"
              onChange={(event) => setEmail(event.target.value)}
              disabled={busy}
            />
          </div>
          {detail.permissions.canManageRoles && (
            <div className="grid gap-2">
              <Label htmlFor="member-role">Role</Label>
              <Select value={inviteRole} onValueChange={(value) => setInviteRole(value as 'member' | 'manager')} disabled={busy}>
                <SelectTrigger id="member-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <Button type="submit" disabled={busy || !email.trim()} className="sm:w-28">Add</Button>
        </form>
      )}

      <section>
        <SectionTitle>Projects</SectionTitle>
        {detail.projects.length === 0 ? (
          <p className="text-sm text-muted-foreground">No projects yet</p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {detail.projects.map((project) => (
              <li key={project.id}>
                <Link
                  to="/$orgSlug/projects/$projectId"
                  params={{ orgSlug: labs.organization.slug, projectId: project.id }}
                  className="block truncate p-4 font-medium hover:bg-muted/60 outline-[hsl(var(--color-ring))] focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-[-2px]"
                >
                  {project.title}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <AlertDialog open={Boolean(pendingMember)} onOpenChange={(open) => { if (!open) setPendingRole(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingIsSelf ? 'Step down as owner' : 'Make owner'}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingIsSelf
                ? `You will become a ${pendingRole?.role ?? 'manager'}.`
                : `${pendingMember?.name} will be able to change roles and delete projects.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy || !pendingRole}
              onClick={() => {
                if (!pendingRole) return;
                void run(
                  () => labsApi.setTeamRole(detail.team.id, pendingRole.userId, pendingRole.role),
                  pendingIsSelf ? 'Role changed' : 'Owner added',
                );
              }}
            >
              {pendingIsSelf ? 'Step down' : 'Make owner'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={claiming} onOpenChange={setClaiming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Become owner</AlertDialogTitle>
            <AlertDialogDescription>
              You will become the owner of {detail.team.name}. Departed owners are removed from the team.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={() => void run(() => labsApi.claimTeamOwnership(detail.team.id), 'You are now the owner')}
            >
              Become owner
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
